/**
 * PresentationProgramView React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2020 contexagon GmbH
 */


import React from 'react';
import PropTypes from 'prop-types';
import PresentationFileView from "./PresentationFileView";
import PresentationFolderView from "./PresentationFolderView";
import PresentationProgramSelectionView from "./PresentationProgramSelectionView";
import IdleModeView from "./IdleModeView";
import PreludeView from "./PreludeView";
import AnimatedUpdate from "../../animation/AnimatedUpdate";


/**
 * presentation program view
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
function PresentationProgramView(props) {

    const navigationIndex = props.navigationIndex || [];
    const rootNavigationIndex = navigationIndex[0];
    const programId = props.presentationProgram ? props.presentationProgram.id : null;

    if (!rootNavigationIndex) {

        return <AnimatedUpdate tag={"presentation-idle:" + programId} {...props.animationProps}>
            <IdleModeView
                screenIndex={props.screenIndex}
                boxName={props.boxName}
                presentationProgram={props.presentationProgram}
                findReferencedObjects={props.findReferencedObjects}
            />
        </AnimatedUpdate>;
    }
    else if (rootNavigationIndex === 1) {

        return <AnimatedUpdate tag={"presentation-prelude:" + programId} {...props.animationProps}>
            <PreludeView
                screenIndex={props.screenIndex}
                boxName={props.boxName}
                presentationProgram={props.presentationProgram}
                findReferencedObjects={props.findReferencedObjects}
            />
        </AnimatedUpdate>;
    }
    else {

        const presentationProgram = props.presentationProgram || {};
        const presentationProgramFields = presentationProgram.fields || {};
        const presentationItems = presentationProgramFields['items'] || [];
        const itemNavigationIndex = navigationIndex[1];

        if (presentationItems.length > 1 && !itemNavigationIndex) {
            return <AnimatedUpdate tag={"presentation-selection:" + programId} {...props.animationProps}>
                <PresentationProgramSelectionView
                    screenIndex={props.screenIndex}
                    presentationProgram={props.presentationProgram}
                    findReferencedObjects={props.findReferencedObjects}
                />
            </AnimatedUpdate>;
        }

        const presentationItemIndex = itemNavigationIndex ? itemNavigationIndex - 1 : 0;
        const presentationItemId = presentationItems[presentationItemIndex];
        const presentationItem = props.findReferencedObjects(presentationItemId);

        if (!presentationItem) {
            return <div>item missing</div>;
        }
        else if (presentationItem.type === 'presentationfolder') {
            const subNavigationIndex = navigationIndex[2] || 0;
            return <AnimatedUpdate tag={"presentation-folder:" + presentationItemId + "/" + subNavigationIndex} {...props.animationProps}>
                <PresentationFolderView
                    screenIndex={props.screenIndex}
                    presentationFolder={presentationItem}
                    navigationIndex={navigationIndex.slice(2)}
                    findReferencedObjects={props.findReferencedObjects}
                    paused={props.paused}
                    playbackPosition={props.playbackPosition}
                    onPlaybackEnded={props.onPlaybackEnded}
                />
            </AnimatedUpdate>;
        }
        else if (presentationItem.type === 'presentationfile') {
            return <AnimatedUpdate tag={"presentation-file:" + presentationItemId} {...props.animationProps}>
                <PresentationFileView
                    screenIndex={props.screenIndex}
                    presentationFile={presentationItem}
                    navigationIndex={navigationIndex.slice(3)}
                    findReferencedObjects={props.findReferencedObjects}
                    paused={props.paused}
                    playbackPosition={props.playbackPosition}
                    onPlaybackEnded={props.onPlaybackEnded}
                />
            </AnimatedUpdate>;
        }
        else {
            return <AnimatedUpdate tag={"invalid"} {...props.animationProps}>
                <div>invalid item</div>
            </AnimatedUpdate>;
        }
    }
}

PresentationProgramView.propTypes = {
    screenIndex: PropTypes.number,
    presentationProgram: PropTypes.object,
    navigationIndex: PropTypes.arrayOf(PropTypes.number),
    findReferencedObjects: PropTypes.func.isRequired,
    animationProps: PropTypes.object,
    paused: PropTypes.bool,
    playbackPosition: PropTypes.number,
    onPlaybackEnded: PropTypes.func
};

export default PresentationProgramView;
